/**
 * Strom und Erzeugung — der Bereich aus `ui_kits/dashboard/StromErzeugungScreen.jsx`,
 * mit Werten seit HM-012, nach dem Entwurf gebaut seit HM-076.
 *
 * **Die Werte kommen aus dem Messkonzept**, nicht aus einem Zähler: Netzbezug,
 * Erzeugung, Einspeisung, Eigenverbrauch und Verbrauch sind Ergebnisgrößen der
 * Rechnung, die unter „Messkonzept" eingerichtet ist. Ohne Konzept gibt es hier
 * nichts zu zeigen — und der Weg dorthin steht an der leeren Fläche.
 *
 * **Der Zeitraum ist ein Kalenderzeitraum der Anlage** — Tag, Woche, Monat,
 * Jahr, gewählt in der Kopfleiste, aufgelöst vom Server in der Zeitzone der
 * Anlage (Regel 3). Er steht in der Adresse: „der März in diesem Haus" ist ein
 * Link.
 *
 * **Energie und Leistung zeigen dieselben Größen** (HM-095): Balken in
 * Kilowattstunden, Linien in Kilowatt, derselbe Ansichten-Umschalter, dieselben
 * Farben. Bis dahin zeigte die Leistung eine Linie je Zähler unter dem Namen
 * seines Steckplatzes — in einem Mieterstromhaus zwölf davon, alle in derselben
 * Farbe, weil die Größe aus dem Schlüssel des Steckplatzes geraten wurde. Der
 * ist opak (ADR 0020); was ein Zähler an seiner Stelle bedeutet, rechnet das
 * Messkonzept, und zwar in Watt genauso wie in Wattstunden (HM-088). Die
 * Kacheln bleiben in beiden Bildern Energie: Eine Summe über Leistungen ist
 * keine Zahl, die etwas bedeutet.
 *
 * **Übernommen ist der Aufbau, nicht der Inhalt.** Auslastung und Ladezustand
 * stehen seit HM-136 in der Karte „Anlage" — aus den Geräten der
 * Erzeugungsanlagen, nicht aus der Bilanz; seit HM-137 steht der Ladezustand
 * auch gestrichelt auf der rechten Achse des System-Diagramms (ADR 0021,
 * Nachtrag). Die Karte „Zähler" zeigt statt
 * „Verbunden" den letzten Wert samt Alter — das ist, was ein Zähler hier
 * beweisen kann.
 *
 * **Verhältnisse sind Verhältnisse der Summen**, nicht Mittelwerte (ADR 0025):
 * Der Autarkiegrad ist Eigenverbrauch ÷ Verbrauch über den ganzen Zeitraum.
 * Der Ring zeigt, wohin die Erzeugung ging; darunter steht er je Bucket im
 * Verlauf — eine eigene Karte mit eigener Achse (ADR 0021).
 *
 * **Die Farben kommen aus der Größenachse** (`quantity`), nie aus der Sparte
 * (ADR 0013).
 */
import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  MetricCard,
  SegmentedControl,
  Select,
  icons,
  quantityColorVar,
  useNarrow,
} from "@hausmeter/ui";
import { Chart, type ChartSeries } from "@hausmeter/ui/chart";
import { formatInstant, formatValue } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";
import { BehindGenerationCard } from "../components/behind-generation.js";
import { ConsumptionComposition, type PartMeter } from "../components/consumption-composition.js";
import { UnitsCompositionCard } from "../components/units-composition.js";
import { Donut } from "../components/donut.js";
import { PERIOD_CAPTIONS } from "../components/period-controls.js";
import { counterResetNote } from "../lib/counter-resets.js";
import { SetupChecklist } from "../components/setup-checklist.js";
import {
  chartPoint,
  qualityNote,
  ENERGY_FORMAT,
  ENERGY_UNIT,
  formatEnergy,
  formatEnergySummary,
  formatPercent,
  formatPowerFull,
  POWER_FORMAT,
  POWER_UNIT,
  RATIO_FORMAT,
} from "../lib/energy-format.js";
import { tariffCaption, tariffParts } from "../lib/grid-tariffs.js";
import { formatTimeAgo } from "../lib/format.js";
import {
  consumptionSplit,
  tenantConsumptionStack,
  type SplitPart,
} from "../lib/consumption-split.js";
import { KeyFigureCards } from "../components/key-figures.js";
import { consumptionLacksUnmeteredGeneration, UNMETERED_GENERATION_NOTE } from "../lib/unmetered-generation.js";
import { PlantCard, plantAssets } from "../components/plant-card.js";
import { generationSplit } from "../lib/generation-split.js";
import { keyFigures } from "../lib/key-figures.js";
import { unitsComposition } from "../lib/units-composition.js";
import { LIVE_MAX_AGE_SECONDS, liveNow } from "../lib/live-now.js";
import { liveFigures, METERS_REPORTING } from "../lib/first-values.js";
import { compositionLive } from "../lib/composition-live.js";
import { directedPower, factLine, meterKindLabel, meterNumberText } from "../lib/meter-labels.js";
import { navigate, routePath, type EnergyPeriod, type Route } from "../lib/router.js";
import {
  useConceptTemplate,
  useGenerationAssets,
  useGenerationAssetState,
  useMeters,
  useSiteConcepts,
  useSiteEnergy,
  useSiteLive,
  useSitePower,
} from "../lib/queries.js";
import { distributionLabel, type PvDistribution } from "../lib/distribution.js";

type Energy = ApiComponents["schemas"]["SiteEnergyResponse"];
type EnergySeries = ApiComponents["schemas"]["SiteEnergySeries"];
type Total = ApiComponents["schemas"]["SiteEnergyTotal"];
type QuantityKey = Total["quantityKey"];
type Concept = ApiComponents["schemas"]["MeasurementConcept"];
type Power = ApiComponents["schemas"]["SitePowerResponse"];
type Subject = ApiComponents["schemas"]["SubjectEnergyTotals"];

const MUTED = { color: "var(--text-muted)", fontSize: 12.5, lineHeight: 1.55, margin: 0 } as const;

type Mode = "energie" | "leistung";

interface ChartSpec {
  readonly id: string;
  readonly label: string;
  readonly subtitle: string;
  readonly quantities: readonly QuantityKey[];
  /** Der Netzbezug als Stapel aus HT und NT (HM-167) — nur als Energie und nur mit Tarifzählwerken. */
  readonly tariffs?: true;
}

/**
 * Welche Größen ein Diagramm zeigt — die Zusammenstellungen des Entwurfs, seit
 * HM-095 für Energie **und** Leistung dieselben.
 */
const CHARTS: readonly ChartSpec[] = [
  {
    id: "system",
    label: "System",
    subtitle: "Verbrauch und Erzeugung",
    quantities: ["consumption", "generation"],
  },
  {
    id: "verbrauch",
    label: "Verbrauch",
    subtitle: "Was im Haus verbraucht wurde",
    quantities: ["consumption"],
  },
  {
    id: "netz",
    label: "Netz",
    subtitle: "Bezug und Einspeisung",
    quantities: ["grid_import", "grid_export"],
  },
  /* Nur, wo der Netzzähler Tarifzählwerke führt (HM-167). Eine eigene Ansicht und
     nicht ein Stapel in „Netz": Das Diagramm stapelt alle Reihen oder keine, und
     die Einspeisung gehört nicht obenauf. */
  {
    id: "tarife",
    label: "HT/NT",
    subtitle: "Netzbezug nach Tarif",
    quantities: ["grid_import"],
    tariffs: true,
  },
  {
    id: "pv",
    label: "Erzeugung",
    subtitle: "Was die Anlage erzeugt hat",
    quantities: ["generation"],
  },
  {
    id: "allgemein",
    label: "Allgemeinstrom",
    subtitle: "Was der Zähler des Allgemeinstroms zählt",
    quantities: ["common_area"],
  },
  /* Nur in einem Konzept mit weiteren Verbrauchern (HM-110): der Haushalt ohne Wallbox und Wärmepumpe.
     Steht er als oberster Streifen im gestapelten Verbrauch (HM-118), entfällt die eigene Ansicht. */
  {
    id: "rest",
    label: "Übriger Verbrauch",
    subtitle: "Was im Haus ohne die gemessenen Verbraucher verbraucht wurde",
    quantities: ["residual_consumption"],
  },
];

export function ElectricityScreen({
  siteId,
  siteTimeZone,
  period,
  date,
}: {
  siteId: string | null;
  /** Die Zeitzone der Anlage, nie die des Browsers. */
  siteTimeZone: string;
  period: EnergyPeriod;
  date: string | null;
}) {
  const concepts = useSiteConcepts(siteId);
  const active = concepts.data?.concepts.find((concept) => concept.status === "active") ?? null;
  const [mode, setMode] = useState<Mode>("energie");
  const query = { period, date };
  const energy = useSiteEnergy(active === null ? null : siteId, query);
  const power = useSitePower(active === null ? null : siteId, query, mode === "leistung");
  const live = useSiteLive(active === null ? null : siteId);

  if (siteId === null) return null;
  if (concepts.isPending) return <p style={MUTED}>Wird geladen …</p>;

  /* Ohne Messkonzept rechnet die Anlage nichts — statt einer leeren Karte der Weg
     dorthin (HM-183). */
  if (active === null) {
    return <SetupChecklist siteId={siteId} concept={false} values={false} />;
  }

  /**
   * **Keine Werte im Zeitraum: eine Fläche statt einer Seite voller Striche**
   * (HM-183, UI-Durchsicht U4). Zwei Fälle, und die Momentanwerte unterscheiden
   * sie: Hat noch kein Zähler des Konzepts je etwas gemeldet, fehlt ein Schritt
   * der Einrichtung — dann die Liste. Melden die Zähler, ist der Zeitraum nur noch
   * nicht gerechnet (kurz nach Mitternacht, gerade eingerichtet) oder liegt vor
   * der ersten Messung — dann ein Satz und, für einen vergangenen Zeitraum, der
   * Weg zu heute. Ohne die Unterscheidung stünde die Einrichtungsliste jede Nacht
   * an jeder Anlage.
   */
  const empty = energy.data !== undefined && energy.data.series.every((s) => s.points.length === 0);
  if (empty && live.data !== undefined && live.data.values.length === 0) {
    return <SetupChecklist siteId={siteId} concept values={false} delivering={false} />;
  }
  if (empty && live.data !== undefined) {
    return (
      <Card>
        <EmptyState
          title={date === null ? "Noch keine Werte für diesen Zeitraum" : "Für diesen Zeitraum liegen keine Werte vor"}
          icon={<icons.IconActivity size={30} />}
          size="md"
          action={
            date === null ? undefined : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate({ kind: "electricity", siteId, period, date: null })}
              >
                Zu heute
              </Button>
            )
          }
        >
          {date === null ? METERS_REPORTING : "Aus dieser Zeit hat kein Zähler des Messkonzepts etwas gemeldet."}
        </EmptyState>
        {date === null ? <LiveFigures conceptNow={live.data.conceptNow} timeZone={siteTimeZone} /> : null}
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {energy.isError && <Alert tone="danger">Die Werte konnten nicht geladen werden.</Alert>}
      {energy.data !== undefined && (
        <EnergyView
          energy={energy.data}
          power={mode === "leistung" ? (power.data ?? null) : null}
          powerPending={mode === "leistung" && power.isPending}
          mode={mode}
          onMode={setMode}
          period={period}
          timeZone={siteTimeZone}
          siteId={siteId}
          concept={active}
        />
      )}
    </div>
  );
}

/**
 * **Die Momentanwerte, solange der Zeitraum noch leer ist** (#75). Verbrauch und Erzeugung als
 * Energie brauchen eine abgeschlossene Viertelstunde; die Leistung jetzt rechnet der Server aus
 * den letzten Werten der Zähler und hat sie eine Minute nach der ersten Messung. Sie zeigt, dass
 * die Einrichtung stimmt, bevor die erste Viertelstunde gerechnet ist.
 */
function LiveFigures({
  conceptNow,
  timeZone,
}: {
  conceptNow: ApiComponents["schemas"]["LiveValuesResponse"]["conceptNow"];
  /** Die Zeitzone der Anlage, nie die des Browsers. */
  timeZone: string;
}) {
  const now = liveNow(conceptNow);
  const figures = liveFigures(now.values);
  if (figures.length === 0 || now.measuredAt === null) return null;
  return (
    <div style={{ borderTop: "1px solid var(--border-subtle)", padding: "14px 4px 2px", display: "flex", flexDirection: "column", gap: 10 }}>
      <p style={MUTED}>Leistung jetzt · Stand {formatInstant(new Date(now.measuredAt), timeZone, undefined, "time")} Uhr</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))", gap: 12 }}>
        {figures.map((figure) => (
          <div key={figure.key} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{figure.label}</span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontVariantNumeric: "tabular-nums",
                fontSize: 20,
                fontWeight: 600,
                color: `var(--hm-quantity-${figure.key.replaceAll("_", "-")})`,
              }}
            >
              {formatPowerFull(figure.watts)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EnergyView({
  energy,
  power,
  powerPending,
  mode,
  onMode,
  period,
  timeZone,
  siteId,
  concept,
}: {
  energy: Energy;
  power: Power | null;
  powerPending: boolean;
  mode: Mode;
  onMode: (mode: Mode) => void;
  period: EnergyPeriod;
  timeZone: string;
  siteId: string;
  concept: Concept;
}) {
  const narrow = useNarrow(1040);
  const tight = useNarrow(620);
  /* Unter 380 px passen vier Ansichten nicht nebeneinander in die Karte — ihr
     `overflow: hidden` schnitt „Erzeugung" ab. Dann ein Auswahlfeld, wie beim
     Zeitraum. */
  const tiny = useNarrow(380);
  const [chartId, setChartId] = useState("system");

  const totalOf = (key: QuantityKey): Total | undefined =>
    energy.totals.find((t) => t.quantityKey === key);
  const seriesOf = (key: QuantityKey): EnergySeries | undefined =>
    energy.series.find((s) => s.quantityKey === key);
  const has = (key: QuantityKey): boolean => energy.series.some((s) => s.quantityKey === key);
  const hasConsumption = has("consumption");
  const tariffs = tariffParts(energy.gridImportTariffs);
  /* Ein Rücksprung ist sonst eine Lücke ohne Grund (HM-180). */
  const resetNote = counterResetNote(energy.counterResets);
  const hasGeneration = has("generation");

  /* Welche Ansichten es gibt, hängt am Bild: Ein Zähler kann Stände liefern und
     keine Leistung. Solange die Leistung lädt, bleibt die Liste der
     Energieansicht stehen — eine Auswahl, die beim Umschalten kurz leer wird
     und danach wieder erscheint, ist schlimmer als eine, die einen Augenblick
     zu viel anbietet. */
  const hasPower = (key: QuantityKey): boolean =>
    power !== null && power.series.some((s) => s.quantityKey === key && s.points.length > 0);
  /**
   * **Der Verbrauch eines Hauses mit Verbrauchern ist ein Stapel** (HM-118):
   * je Verbraucher ein Streifen, obenauf der übrige Verbrauch — die Höhe bleibt
   * der Verbrauch. Nur als Energie: Die Leistung eines Verbrauchers ist eine
   * eigene Messung und keine Aufteilung dieser Kurve.
   *
   * **Im Mieterstrom je Wohnung** (HM-118): unten jede Wohneinheit, darüber die
   * weiteren Verbraucher, als Helligkeitsreihe des Verbrauchs ohne Grenze —
   * die Karte darunter fasst die Wohnungen dagegen zusammen, sie stehen einzeln
   * in ihrer Tabelle.
   */
  const composition = consumptionSplit(energy);
  const stackConsumption = composition !== null && composition.stackable;
  const tenantStack = tenantConsumptionStack(energy);
  const consumptionBands: readonly ChartSeries[] | null =
    tenantStack !== null
      ? tenantStack.map((band) => ({
          id: band.key,
          label: band.label,
          quantity: "consumption",
          fade: band.fade,
          mark: "bars",
          points: band.series.points.map(chartPoint),
        }))
      : composition !== null && composition.stackable
        ? composition.parts.map((part) => ({
            id: part.key,
            label: part.label,
            quantity: part.shade!,
            mark: "bars",
            points: part.series!.points.map(chartPoint),
          }))
        : null;
  const energyCharts = CHARTS.filter(
    (chart) =>
      chart.quantities.every(has) &&
      !(chart.id === "rest" && stackConsumption) &&
      (chart.tariffs !== true || tariffs.length > 0),
  );
  const charts =
    mode === "leistung" && power !== null
      ? CHARTS.filter(
          (chart) =>
            chart.quantities.every(hasPower) &&
            !(chart.id === "rest" && stackConsumption) &&
            chart.tariffs !== true,
        )
      : energyCharts;
  const chart = charts.find((c) => c.id === chartId) ?? charts[0];
  const stacked = mode === "energie" && chart?.id === "verbrauch" && consumptionBands !== null;
  const byUnits = tenantStack !== null;
  /**
   * **Der Ladezustand im System-Diagramm** (HM-137, Entwurf): gestrichelt auf
   * einer rechten Achse 0…100 %, nur in der Ansicht „System" und nur als Energie
   * — er steht im Raster der Balken, die Leistung hat ein feineres. Mehrere
   * Batterien bekommen keine Linie, sondern einen Satz (ADR 0040).
   */
  const soc = energy.stateOfCharge;
  const socLine: ChartSeries | null =
    mode === "energie" &&
    chart?.id === "system" &&
    soc?.status === "single" &&
    soc.points.length > 0
      ? {
          id: "state_of_charge",
          label: energy.resolution === "15m" ? "Ladezustand" : "Ladezustand (Mittel)",
          axis: "right",
          dashed: true,
          mark: "line",
          points: soc.points.map((point) => ({ at: new Date(point.t).getTime(), value: point.v })),
        }
      : null;

  const from = new Date(energy.from).getTime();
  const to = new Date(energy.to).getTime();
  const caption = PERIOD_CAPTIONS[period];

  const meters = useMeters(siteId, true);
  const live = useSiteLive(siteId);
  /* Momentanwerte hängen nicht am gewählten Zeitraum — „jetzt" ist jetzt, ob
     man die Woche oder das Jahr betrachtet. Sie stehen in der Fußzeile der
     Kachel, die dieselbe Größe summiert; eine eigene Karte dafür kostete eine
     Bildschirmhöhe für vier Zahlen (Entwurf). */
  const now = liveNow(live.data?.conceptNow).values;
  /* Ohne „jetzt" davor (Entwurf): Was oben rechts steht, sagt die Zeile über den Kacheln. */
  const asideOf = (key: QuantityKey): string | undefined =>
    now[key] === undefined ? undefined : formatPowerFull(now[key]!);
  /** Woraus die Erzeugung besteht — nur mit Speicher (`lib/generation-split.ts`). */
  const split = generationSplit(energy, now, live.data?.behindGeneration);
  const assets = plantAssets(useGenerationAssets(siteId).data?.items ?? []);

  const gridImport = totalOf("grid_import");
  const generation = totalOf("generation");
  const gridExport = totalOf("grid_export");
  const selfUse = totalOf("self_consumption");
  const consumption = totalOf("consumption");
  const autarky = totalOf("autarky_ratio");
  const selfShare = totalOf("self_consumption_ratio");
  const autarkySeries = seriesOf("autarky_ratio");

  /* Der Zähler eines Verbrauchers: die Ausgabe seines Verbrauchs zeigt auf den
     Knoten seines Steckplatzes, der Knoten auf die Messstelle, die Messstelle
     auf den Zähler, der gerade an ihr hängt. */
  const meterRowOfSubject = (subjectKey: string, quantityKey: QuantityKey) => {
    const output = concept.outputs.find(
      (o) => o.subjectKey === subjectKey && o.quantityKey === quantityKey,
    );
    const node = concept.nodes.find((n) => n.key === output?.nodeKey);
    return (meters.data?.items ?? []).find(
      (m) =>
        node?.meteringPointId != null &&
        m.meteringPointId === node.meteringPointId &&
        (m.status === "active" || m.status === "draft"),
    );
  };
  const meterOfSubject = (subjectKey: string, quantityKey: QuantityKey): PartMeter | null => {
    const meter = meterRowOfSubject(subjectKey, quantityKey);
    return meter === undefined ? null : { label: meter.label, number: meterNumberText(meter) };
  };
  const meterOf = (part: SplitPart): PartMeter | null => meterOfSubject(part.key, "load_consumption");
  /* Die Wohneinheiten im Aufbau der Zusammensetzung (HM-145) — nur im Mieterstrom. */
  const units = composition?.kind === "house" ? null : unitsComposition(energy.subjects);

  /*
   * **Die Leistung jetzt je Teil** (HM-146, seit HM-148 vom Server): die
   * Ergebnisgrößen des Messkonzepts, mit den Momentanwerten aller seiner Zähler
   * ausgewertet (`conceptNow`). Ein Teil ohne eigenen Zähler — die selbst
   * genutzte Wohnung bei Eigenverbrauch — hat seine Leistung nur so; am Zähler
   * gesucht fehlte sie, und mit ihr die ganze Zeile. Nur, solange der Zeitraum
   * das Jetzt enthält — unter dem gestrigen Tag stünde sonst eine Leistung,
   * die mit ihm nichts zu tun hat.
   */
  const nowMs = Date.now();
  const current = from <= nowMs && nowMs < to;
  const conceptNow = live.data?.conceptNow ?? null;
  const wattsOf = (quantityKey: QuantityKey, subjectKey: string | null): number | null =>
    conceptNow?.outputs.find((o) => o.quantityKey === quantityKey && o.subjectKey === subjectKey)
      ?.watts ?? null;
  /* Die Wohnungen zusammen sind keine Größe des Konzepts — die Summe der Wohnungen, nur mit allen. */
  const unitKeys = energy.subjects.filter((s) => s.kind === "unit").map((s) => s.subjectKey);
  const sumOfUnits = (quantityKey: QuantityKey): number | null => {
    const each = unitKeys.map((key) => wattsOf(quantityKey, key));
    return each.length === 0 || each.some((w) => w === null)
      ? null
      : each.reduce<number>((a, w) => a + Math.max(w ?? 0, 0), 0);
  };
  const compositionNow =
    !current || composition === null || conceptNow === null
      ? null
      : compositionLive(
          composition.parts.map((part) => ({ key: part.key, rest: part.kind === "rest" })),
          (key) => {
            const part = composition.parts.find((p) => p.key === key);
            return part?.kind === "units"
              ? sumOfUnits("unit_consumption")
              : wattsOf("load_consumption", key);
          },
          wattsOf("consumption", null),
          {
            /* Woher die Leistung kommt (HM-150): der PV-Anteil je Teil nach der Regel des Konzepts. */
            of: (key) => {
              const part = composition.parts.find((p) => p.key === key);
              return part?.kind === "units"
                ? sumOfUnits("unit_pv_share")
                : wattsOf("load_pv_share", key);
            },
            totalW: wattsOf("self_consumption", null),
          },
        );
  const unitsNow =
    !current || units === null || conceptNow === null
      ? null
      : compositionLive(
          units.rows.map((row) => ({ key: row.key, rest: false })),
          (key) => wattsOf("unit_consumption", key),
          null,
          { of: (key) => wattsOf("unit_pv_share", key), totalW: null },
        );

  /**
   * **Die Fußzeile folgt dem Entwurf** (HM-138): der Zeitraum an Netzbezug und
   * Verbrauch, „ins Netz" an der Einspeisung, „selbst genutzt" am
   * Eigenverbrauch. **Eine Abdeckung steht nicht dabei** (Auftraggeber): Wie viel
   * des Zeitraums vorliegt, zeigt das Diagramm darunter — Lücken und blasse
   * Teilsummen. Ersatzwerte stehen dagegen weiter da: Die sieht man der Summe
   * nirgends an (Regel 8, ADR 0034).
   */
  const FOOTERS: Partial<Record<QuantityKey, string>> = {
    grid_import: caption,
    consumption: caption,
    grid_export: "ins Netz",
    self_consumption: "selbst genutzt",
  };
  /**
   * **Der spezifische Ertrag an der Erzeugung** (HM-122): kWh je installiertem
   * kWp, aus der Nennleistung der Erzeugungsanlagen hinter den Erzeugungszählern.
   * Die API sagt `null`, wo sich daraus keine Aussage bilden lässt — dann steht
   * nichts da, kein „—".
   */
  const capacity = energy.generationCapacity;
  /* Steht ein Speicher hinter dem Erzeugungszähler, misst er netto und die API
     nennt dort keinen Ertrag je kWp; dann kommt er aus der PV brutto der Geräte
     (HM-133) — und sagt das. */
  /* Nur, wenn die Geräte den ganzen Zeitraum abdecken — sonst wäre es der Ertrag
     seit ihrem ersten Wert, auf jedem Zeitraum derselbe (HM-139). */
  const devicesPv =
    energy.behindGeneration?.partial === false ? (energy.behindGeneration.pv ?? null) : null;
  /* Über ein Jahr ganze Zahlen, über einen Tag zählen die Nachkommastellen. */
  const kwhPerKwp = (value: number) =>
    new Intl.NumberFormat("de-DE", { maximumFractionDigits: value < 10 ? 2 : 0 }).format(value);
  /* Er steht seit HM-138 in der Unterzeile der Karte „Anlage", neben der
     Nennleistung — die Kachel „Erzeugung" des Entwurfs hat keinen Platz dafür. */
  const yieldNote =
    capacity !== null && capacity.specificYieldKwhPerKwp !== null
      ? `${kwhPerKwp(capacity.specificYieldKwhPerKwp)} kWh/kWp ${caption}`
      : devicesPv !== null &&
          devicesPv.specificYieldKwhPerKwp !== null &&
          devicesPv.peakPowerKwp !== null
        ? `${kwhPerKwp(devicesPv.specificYieldKwhPerKwp)} kWh/kWp brutto ${caption}`
        : null;

  const unmeteredGeneration = consumptionLacksUnmeteredGeneration(concept.outputs.map((output) => output.quantityKey));
  const captionOf = (total: Total): string | undefined =>
    [
      FOOTERS[total.quantityKey] ?? null,
      total.quantityKey === "grid_import" ? tariffCaption(tariffs, formatEnergy) : null,
      /* Einspeisung ohne gemessene Erzeugung: dem Verbrauch fehlt der PV-Strom, der im Haus blieb (HM-171). */
      total.quantityKey === "consumption" && unmeteredGeneration ? UNMETERED_GENERATION_NOTE : null,
      qualityNote(total),
    ]
      .filter((part): part is string => part !== null)
      .join(" · ") || undefined;
  const tile = (total: Total | undefined, icon: React.ReactNode) =>
    total === undefined ? null : (
      <MetricCard
        key={total.quantityKey}
        commodity="electricity"
        quantity={total.quantityKey}
        icon={icon}
        label={total.label}
        value={total.value === null ? null : formatEnergy(total.value)}
        unit={ENERGY_UNIT}
        caption={captionOf(total)}
        aside={asideOf(total.quantityKey)}
      />
    );

  /**
   * **Die Kennzahlen-Karten des Entwurfs** (HM-156, `components/key-figures.tsx`):
   * Erzeugungsanlagen · Netzbezug · Verbrauch. Die erste zeigt den
   * Erzeugungszähler, darunter PV und Akku; der Eigenverbrauch steht als Teil der
   * dritten — Kacheln dafür entfallen. **Seit HM-176 an jeder Anlage**, mit den
   * Karten, die sie führt: ohne Erzeugung Netzbezug und Verbrauch, ohne Verbrauch
   * Erzeugungsanlagen und Netzbezug. Vorher fiel eine Anlage ohne Erzeugung auf
   * die alten Kacheln zurück.
   *
   * **Der Ladezustand ist der von jetzt**, aus dem Gerät der einen Batterie
   * (`stateOfCharge.generationAssetId`), mit derselben Frist wie jeder
   * Momentanwert — nicht der letzte Abschnitt des Zeitraums: Unter „Jahr" wäre
   * das das Mittel eines Monats, und unter dem gestrigen Tag sein Abend.
   */
  const socAssetId =
    energy.stateOfCharge?.status === "single" ? energy.stateOfCharge.generationAssetId : null;
  const socState = useGenerationAssetState(split !== null ? socAssetId : null).data;
  const socNow =
    socState !== undefined &&
    socState.measuredAt !== null &&
    Date.now() - new Date(socState.measuredAt).getTime() <= LIVE_MAX_AGE_SECONDS * 1000
      ? (socState.battery?.soc ?? null)
      : null;
  const figures = keyFigures(
    {
      present: {
        consumption: consumption !== undefined,
        gridImport: gridImport !== undefined,
        gridExport: gridExport !== undefined,
      },
      generationWh: generation?.value ?? null,
      consumptionWh: consumption?.value ?? null,
      selfUseWh: selfUse?.value ?? null,
      gridImportWh: gridImport?.value ?? null,
      gridExportWh: gridExport?.value ?? null,
      autarky: autarky?.value ?? null,
    },
    split,
    now,
    tariffs,
  );
  const noteOf = (...parts: (Total | undefined)[]): string | undefined =>
    parts.map((part) => (part === undefined ? null : qualityNote(part))).find((note) => note !== null) ??
    undefined;
  /* Beginnen die Geräte hinter dem Zähler erst im Zeitraum, sind PV und Akku eine
     Teilsumme — die große Zahl (der Zähler) ist es nicht, deshalb steht es dabei. */
  const withPartial = (note: string | undefined): string | undefined =>
    split?.partial === true
      ? [note, "PV und Akku ab dem ersten Wert im Zeitraum"].filter(Boolean).join(" · ")
      : note;
  /* Was die Karten nicht tragen: Allgemeinstrom. Ladung und Entladung eines Speichers
     stehen in der ersten Karte, aus den Geräten hinter dem Erzeugungszähler (#40). */
  const extraTiles = [tile(totalOf("common_area"), <icons.IconBuilding size={16} />)].filter(
    (entry) => entry !== null,
  );

  const tileGrid = (entries: readonly React.ReactNode[]) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: tight
          ? "repeat(auto-fit, minmax(min(100%, 150px), 1fr))"
          : `repeat(${Math.max(2, Math.min(4, entries.length))}, minmax(0, 1fr))`,
        gap: 16,
      }}
    >
      {entries}
    </div>
  );

  return (
    <>
      {figures !== null && (
        <KeyFigureCards
          figures={figures}
          stateOfCharge={socNow}
          notes={{
            supply: withPartial(noteOf(generation)),
            grid: noteOf(gridImport, gridExport),
            /* Einspeisung ohne gemessene Erzeugung: dem Verbrauch fehlt der PV-Strom, der im Haus blieb (HM-171). */
            consumption:
              [unmeteredGeneration ? UNMETERED_GENERATION_NOTE : undefined, noteOf(consumption, selfUse)]
                .filter(Boolean)
                .join(" · ") || undefined,
          }}
        />
      )}
      {/* Was keine Karte trägt — Allgemeinstrom —, bleibt eine Kachel. */}
      {extraTiles.length > 0 && tileGrid(extraTiles)}

      {resetNote !== null && (
        <Alert
          tone="warning"
          action={
            resetNote.meterId === null ? undefined : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  navigate({
                    kind: "meter",
                    siteId,
                    meterId: resetNote.meterId!,
                    tab: "overview",
                    register: null,
                    range: "7d",
                  })
                }
              >
                Zähler öffnen
              </Button>
            )
          }
        >
          <strong>{resetNote.title}.</strong> {resetNote.text}
        </Alert>
      )}


      <Card
        title={chart?.label ?? "Verlauf"}
        subtitle={`${stacked ? (byUnits ? "Nach Wohneinheiten und Verbrauchern aufgeteilt" : "Nach Verbraucher aufgeteilt") : `${chart?.subtitle ?? "Energie"}${socLine !== null ? ", Ladezustand" : ""}`} · ${caption} · ${mode === "leistung" ? POWER_UNIT : ENERGY_UNIT}${socLine !== null ? " / %" : ""}`}
        action={
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {charts.length > 1 &&
              (tiny ? (
                <Select
                  label="Ansicht"
                  labelHidden
                  size="sm"
                  value={chart?.id ?? "system"}
                  onChange={(event) => setChartId(event.target.value)}
                  options={charts.map((c) => ({ value: c.id, label: c.label }))}
                />
              ) : (
                <SegmentedControl
                  label="Ansicht"
                  size="sm"
                  value={chart?.id ?? "system"}
                  onChange={setChartId}
                  options={charts.map((c) => ({ value: c.id, label: c.label }))}
                />
              ))}
            <SegmentedControl
              label="Größe"
              size="sm"
              value={mode}
              onChange={onMode}
              options={[
                { value: "energie", label: "Energie" },
                { value: "leistung", label: "Leistung" },
              ]}
            />
          </div>
        }
      >
        {stacked && consumptionBands !== null && (
          <>
            <Chart
              series={consumptionBands}
              stacked
              stackTotalLabel="Verbrauch"
              from={from}
              to={to}
              bucket={energy.resolution}
              mergeHours
              format={ENERGY_FORMAT}
              timeZone={timeZone}
              height={230}
              legend
              label={`Verbrauch nach ${byUnits ? "Wohneinheiten und Verbrauchern" : "Verbraucher"}, ${caption}`}
              emptyMessage="Im Zeitraum liegen keine Werte vor."
            />
            <p style={{ ...MUTED, marginTop: 10 }}>
              Gestapelt, nicht gruppiert: Die Streifen sind Teile eines Ganzen, die Höhe eines
              Balkens bleibt der Verbrauch.
              {byUnits
                ? " Unten je Wohneinheit ein Streifen, darüber die weiteren Verbraucher — alles gemessen. Alle Streifen sind Verbrauch und tragen deshalb Stufen derselben Farbe; welcher welcher ist, steht im Ablesefenster."
                : " Der oberste Streifen ist der gerechnete Rest."}
            </p>
          </>
        )}
        {mode === "energie" && chart?.tariffs === true && (
          <>
            <Chart
              series={tariffs.map(
                (part) =>
                  ({
                    id: part.role,
                    label: part.label,
                    quantity: "grid_import",
                    fade: part.fade,
                    mark: "bars",
                    points: part.tariff.points.map(chartPoint),
                  }) satisfies ChartSeries,
              )}
              stacked
              stackTotalLabel="HT + NT"
              from={from}
              to={to}
              bucket={energy.resolution}
              mergeHours
              format={ENERGY_FORMAT}
              timeZone={timeZone}
              height={230}
              legend
              label={`Netzbezug nach Tarif, ${caption}`}
              emptyMessage="Im Zeitraum liegen keine Werte vor."
            />
            <p style={{ ...MUTED, marginTop: 10 }}>
              Aus den Tarifzählwerken des Netzzählers: Tarif 1 (HT) und Tarif 2 (NT). Jedes wird
              für sich abgelesen — die Summe kann um wenige Wattstunden vom Netzbezug abweichen.
            </p>
          </>
        )}
        {mode === "energie" && chart !== undefined && !stacked && chart.tariffs !== true && (
          <Chart
            series={[
              ...chart.quantities.map((key) => {
                const entry = seriesOf(key)!;
                return {
                  id: entry.quantityKey,
                  label: entry.label,
                  quantity: entry.quantityKey,
                  mark: "bars",
                  points: entry.points.map(chartPoint),
                } satisfies ChartSeries;
              }),
              ...(socLine === null ? [] : [socLine]),
            ]}
            from={from}
            to={to}
            bucket={energy.resolution}
            mergeHours
            format={ENERGY_FORMAT}
            {...(socLine === null
              ? {}
              : { rightFormat: RATIO_FORMAT, rightRange: [0, 1] as const })}
            timeZone={timeZone}
            height={230}
            legend
            label={`${chart.label}${socLine !== null ? " mit Ladezustand" : ""}, ${caption}`}
            emptyMessage="Im Zeitraum liegen keine Werte vor."
          />
        )}
        {mode === "energie" && chart?.id === "system" && soc?.status === "several" && (
          <p style={{ ...MUTED, marginTop: 10 }}>
            Der Ladezustand fehlt hier: An dieser Anlage hängen mehrere Batterien, und ein Mittel
            hätte keine von ihnen. Je Batterie steht er auf der Seite ihrer Erzeugungsanlage.
          </p>
        )}
        {mode === "energie" && chart === undefined && (
          <p style={MUTED}>Dieses Messkonzept liefert keine Größe, die sich hier zeichnen ließe.</p>
        )}
        {mode === "leistung" && (
          <PowerChart
            power={power}
            pending={powerPending}
            chart={chart}
            timeZone={timeZone}
            caption={caption}
          />
        )}
      </Card>

      {composition !== null && (
        <ConsumptionComposition
          split={composition}
          caption={caption}
          meterOf={meterOf}
          live={compositionNow}
        />
      )}

      {hasConsumption && hasGeneration && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
            gap: 16,
          }}
        >
          <Card title="Verwendung" subtitle="Wohin ging die Erzeugung?">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 22,
                paddingTop: 4,
                flexWrap: "wrap",
              }}
            >
              <Donut
                segments={[
                  {
                    value: selfUse?.value ?? 0,
                    color: "var(--hm-quantity-self-consumption)",
                    label: "Eigenverbrauch",
                  },
                  {
                    value: gridExport?.value ?? 0,
                    color: "var(--hm-quantity-grid-export)",
                    label: "Einspeisung",
                  },
                ]}
                centerLabel={selfShare?.value == null ? "–" : formatPercent(selfShare.value)}
                centerSub="selbst genutzt"
              />
              <div
                style={{
                  flex: 1,
                  minWidth: 160,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <LegendRow
                  color="var(--hm-quantity-self-consumption)"
                  label="Eigenverbrauch"
                  total={selfUse}
                />
                <LegendRow
                  color="var(--hm-quantity-grid-export)"
                  label="Einspeisung"
                  total={gridExport}
                />
                {/* Kein Netzbezug hier (UI-Durchsicht U11): Die Karte fragt, wohin die
                    Erzeugung ging, und der Netzbezug kam nicht aus ihr. */}
              </div>
            </div>
            <div
              style={{
                marginTop: 16,
                paddingTop: 14,
                borderTop: "1px solid var(--border-subtle)",
                display: "flex",
                gap: 20,
                flexWrap: "wrap",
              }}
            >
              {/* Die Eigenverbrauchsquote steht schon groß im Ring — hier nur, was er
                  nicht sagt (UI-Durchsicht U11). */}
              {autarky !== undefined && (
                <RatioBlock
                  label="Autarkiegrad"
                  total={autarky}
                  color="var(--hm-quantity-self-consumption)"
                  formula="Eigenverbrauch ÷ Verbrauch"
                  note="des Verbrauchs selbst gedeckt"
                />
              )}
            </div>
          </Card>

          {/* Rechts die Anlage (Entwurf) — wo es keine mit Gerät gibt, wie bisher
              der Autarkiegrad im Verlauf, statt einer leeren Karte. */}
          {assets.length > 0 && <PlantCard assets={assets} yieldNote={yieldNote} />}
          {assets.length === 0 && autarkySeries !== undefined && (
            <Card title="Autarkiegrad im Verlauf" subtitle={`${caption} · %`}>
              <Chart
                series={[
                  {
                    id: "autarky_ratio",
                    label: autarkySeries.label,
                    quantity: "self_consumption",
                    mark: "line",
                    points: autarkySeries.points.map(chartPoint),
                  },
                ]}
                from={from}
                to={to}
                bucket={energy.resolution}
                format={RATIO_FORMAT}
                timeZone={timeZone}
                height={150}
                label={`Autarkiegrad, ${caption}`}
                emptyMessage="Im Zeitraum liegt kein Autarkiegrad vor."
              />
              {/* Ein eigenes Diagramm statt einer zweiten Achse (ADR 0021). Die Begründung
                  stand hier bis HM-182 als Absatz für den Nutzer — sie gehört in den Code. */}
            </Card>
          )}
        </div>
      )}

      {/* Was hinter den Erzeugungszählern steht, aus den Geräten (HM-133) — nicht in der Bilanz. */}
      {energy.behindGeneration !== null && (
        <BehindGenerationCard
          siteId={siteId}
          behind={energy.behindGeneration}
          netGenerationWh={generation?.value ?? null}
          caption={caption}
        />
      )}

      {/* Im Haus ersetzt die Aufteilung die Tabelle (HM-118); im Mieterstrom bleibt sie
          den Wohneinheiten, und die weiteren Verbraucher stehen in der Aufteilung. */}
      {/* Im Mieterstrom wie die Zusammensetzung (HM-145): ein Balken über die Wohnungen,
          je Wohnung eine Zeile. */}
      {units !== null && (
        <UnitsCompositionCard
          composition={units}
          caption={caption}
          live={unitsNow}
          meterOf={(row) => meterOfSubject(row.key, "unit_consumption")}
          linkOf={(row, label) => {
            if (row.siteAreaId === null) return label;
            const route: Route = {
              kind: "unit",
              siteId,
              unitId: row.siteAreaId,
              tab: "electricity",
              period,
              date: energy.period?.date ?? null,
            };
            return (
              <a
                href={routePath(route)}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                  event.preventDefault();
                  navigate(route);
                }}
                style={{ textDecoration: "none" }}
              >
                {label}
              </a>
            );
          }}
        />
      )}
      {units === null && energy.subjects.length > 0 && composition?.kind !== "house" && (
        <SubjectsCard
          loadsElsewhere={composition !== null}
          siteId={siteId}
          subjects={energy.subjects}
          distribution={energy.distribution}
          caption={caption}
          period={period}
          date={energy.period?.date ?? null}
        />
      )}
      <MetersCard siteId={siteId} concept={concept} />
    </>
  );
}

/**
 * Die Leistung **derselben Größen**, die daneben als Energie stehen (HM-095):
 * dieselbe Ansicht, dieselben Farben, nur Watt statt Wattstunden und eine Linie
 * statt eines Balkens.
 *
 * **Nicht mehr eine Linie je Zähler.** Sie trug den Namen seines Steckplatzes
 * und ihre Farbe kam aus einem Blick auf dessen Schlüssel — in einem
 * Mieterstromhaus heißt der `unit.4f3a…`, und alle zwölf Linien standen in
 * derselben Farbe. Der Schlüssel ist opak (ADR 0020); was ein Zähler an seiner
 * Stelle bedeutet, sagt das Messkonzept, und das rechnet es jetzt aus
 * (`GET /sites/{id}/power`).
 *
 * **Ein Band gibt es dafür nicht mehr.** Das Maximum des Verbrauchs ist nicht
 * die Summe der Maxima seiner Zähler — sie treten nicht gleichzeitig auf. Die
 * Spitzen eines einzelnen Zählers stehen an seinem Datenpunkt.
 */
function PowerChart({
  power,
  pending,
  chart,
  timeZone,
  caption,
}: {
  power: Power | null;
  pending: boolean;
  chart: ChartSpec | undefined;
  timeZone: string;
  caption: string;
}) {
  if (pending || power === null) return <p style={MUTED}>Die Leistung wird geladen …</p>;
  if (power.series.length === 0) {
    return (
      <p style={MUTED}>
        Kein Zähler im Messkonzept hat ein Leistungszählwerk mit Quelle. Die Leistung meldet ein
        Gerät je Zähler als eigenen Wert; gebunden wird sie unter „Zähler".
      </p>
    );
  }
  if (chart === undefined) {
    return (
      <p style={MUTED}>
        Für diesen Zeitraum liegt keine Größe vor, die sich als Leistung zeichnen ließe.
      </p>
    );
  }
  const series = chart.quantities
    .map((key) => power.series.find((entry) => entry.quantityKey === key))
    .filter((entry) => entry !== undefined);
  return (
    <>
      <Chart
        series={series.map((entry) => ({
          id: entry.quantityKey,
          label: entry.label,
          quantity: entry.quantityKey,
          mark: "line",
          points: entry.points.map(chartPoint),
        }))}
        from={new Date(power.from).getTime()}
        to={new Date(power.to).getTime()}
        bucket={power.resolution}
        format={POWER_FORMAT}
        timeZone={timeZone}
        height={230}
        legend
        label={`${chart.label} als Leistung, ${caption}`}
        emptyMessage="Im Zeitraum liegen keine Leistungswerte vor."
      />
      <p style={{ ...MUTED, marginTop: 10 }}>
        Die mittlere Leistung je Zeitschritt, nach der Regel des Messkonzepts gerechnet — wie die
        Energie daneben. Fehlt in einem Schritt die Leistung eines beteiligten Zählers, fehlt das
        Ergebnis: Eine halbe Bilanz ist keine.
      </p>
    </>
  );
}

/**
 * Die Zähler des Messkonzepts — die Karte „Zähler" des Entwurfs, mit dem, was
 * ein Zähler hier beweisen kann: seinem letzten Wert samt Alter. „Verbunden"
 * sagt der Entwurf; hier steht, wann zuletzt etwas ankam, denn ein Zähler ist
 * keine Verbindung, sondern eine Quelle, die liefert oder nicht.
 */
function MetersCard({ siteId, concept }: { siteId: string; concept: Concept }) {
  const meters = useMeters(siteId, true);
  const live = useSiteLive(siteId);
  /* Die Vorlage **dieser Fassung**, expandiert: Die des Katalogs kennt nur den ersten
     Erzeugungszähler (#43) und keine Wohneinheit — die übrigen Zähler fehlten in der Liste. */
  const template = useConceptTemplate(concept.id).data ?? null;

  const slotOf = (meteringPointId: string) => {
    const node = concept.nodes.find(
      (n) => n.type === "meter_register" && n.meteringPointId === meteringPointId,
    );
    const input = node === undefined ? undefined : template?.inputs.find((i) => i.key === node.key);
    return input === undefined ? null : (template?.slots.find((s) => s.key === input.slot) ?? null);
  };
  const values = live.data?.values ?? [];
  const rows = (meters.data?.items ?? [])
    .map((meter) => ({
      meter,
      slot: slotOf(meter.meteringPointId),
      power: values.find((v) => v.meterId === meter.id && v.role === "power_total") ?? null,
    }))
    .filter(
      (row) => row.slot !== null && (row.meter.status === "active" || row.meter.status === "draft"),
    );

  return (
    <Card title="Zähler" subtitle="Die Zähler in den Steckplätzen des Messkonzepts">
      {rows.length === 0 && (
        <p style={MUTED}>
          Noch kein Zähler steckt in einem Steckplatz. Die Zuordnung steht unter „Messkonzept".
        </p>
      )}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {rows.map(({ meter, slot, power }, index) => {
          const stale = power !== null && power.ageSeconds > 10 * 60;
          return (
            <div
              key={meter.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "13px 0",
                borderBottom: index < rows.length - 1 ? "1px solid var(--border-subtle)" : "none",
                flexWrap: "wrap",
              }}
            >
              {(() => {
                const look = slotLook(slot);
                return (
                  <span
                    style={{
                      display: "inline-flex",
                      width: 34,
                      height: 34,
                      flexShrink: 0,
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 9,
                      background: `color-mix(in srgb, ${look.color} 16%, transparent)`,
                      color: look.color,
                    }}
                  >
                    {look.icon}
                  </span>
                );
              })()}
              <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong)" }}>
                  {meter.label}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    color: "var(--text-muted)",
                  }}
                >
                  {factLine([meterNumberText(meter), meterKindLabel(meter.kind, meter.commodity), slot?.label], [meter.label])}
                </div>
              </div>
              {power !== null && (
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontVariantNumeric: "tabular-nums",
                    fontSize: 16,
                    fontWeight: 600,
                    color: stale ? "var(--text-faint)" : "var(--text-strong)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {(() => {
                    /* Richtung statt Vorzeichen (UI-Durchsicht U3). */
                    const directed =
                      power.unit === "W"
                        ? directedPower(power.value, slot?.placement === "connection")
                        : { watts: power.value, word: null };
                    return (
                      <>
                        {directed.word !== null && (
                          <span style={{ fontFamily: "var(--font-body)", fontSize: 12.5, fontWeight: 500, color: "var(--text-muted)", marginRight: 6 }}>
                            {directed.word}
                          </span>
                        )}
                        {formatValue(directed.watts, power.unit).full}
                      </>
                    );
                  })()}
                </span>
              )}
              {power === null ? (
                <Badge tone="neutral">keine Leistung gemeldet</Badge>
              ) : stale ? (
                <Badge tone="warning">zuletzt {formatTimeAgo(power.measuredAt)}</Badge>
              ) : (
                <Badge tone="success" dot>
                  liefert · {formatTimeAgo(power.measuredAt)}
                </Badge>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

type Slot = ApiComponents["schemas"]["ConceptTemplate"]["slots"][number];

/**
 * **Symbol und Farbe eines Zählers nach seinem Steckplatz** (Entwurf, HM-138):
 * der Netzzähler mit Blitz in Netzbezugsfarbe, der Erzeugungszähler mit Sonne in
 * Erzeugungsfarbe. Entschieden wird an der **Lage und dem, was der Steckplatz
 * liefert** — nicht am Schlüssel, der ist opak (ADR 0020).
 */
function slotLook(slot: Slot | null): { icon: React.ReactNode; color: string } {
  if (slot?.placement === "connection") {
    return { icon: <icons.IconBolt size={17} />, color: quantityColorVar("grid_import") };
  }
  if (slot?.delivers.includes("Erzeugung")) {
    return { icon: <icons.IconSun size={17} />, color: quantityColorVar("generation") };
  }
  return { icon: <icons.IconGauge size={17} />, color: quantityColorVar("consumption") };
}

/**
 * Wohneinheiten und weitere Verbraucher eines Mieterstrom-Konzepts (HM-080):
 * je Zeile Verbrauch, PV-Anteil und Netzanteil über den Zeitraum. **Nur
 * Summen**, keine Kurven — die Kurve einer Wohnung hat die Wohnung selbst,
 * und der Name führt dorthin. Verbraucher ohne Wohneinheit (Wärmepumpe,
 * Allgemeinstromzähler) stehen darunter ohne Link.
 *
 * **Ein Verbraucher hinter dem Zähler einer Wohnung steht eingerückt unter
 * ihr** (HM-111) — die Wallbox eines Mieters. Er ist ein Teil ihres
 * Verbrauchs und keine weitere Zeile, die sich zu den anderen addiert; unter
 * „Weitere Verbraucher" sähe er genau danach aus. Ob eine Zeile eine Wohnung
 * ist, sagt `kind` und nicht `siteAreaId`: Die trägt er mit.
 */
function SubjectsCard({
  siteId,
  subjects,
  distribution,
  caption,
  period,
  date,
  loadsElsewhere,
}: {
  siteId: string;
  subjects: readonly Subject[];
  /** Die weiteren Verbraucher stehen in der Zusammensetzung des Verbrauchs (HM-118). */
  loadsElsewhere: boolean;
  distribution: PvDistribution | null;
  caption: string;
  period: EnergyPeriod;
  date: string | null;
}) {
  const narrow = useNarrow(620);
  const cell = (subject: Subject, key: QuantityKey) => {
    const total = subject.totals.find((t) => t.quantityKey === key);
    return total === undefined || total.value === null
      ? "–"
      : `${formatEnergy(total.value)} ${ENERGY_UNIT}`;
  };
  const units = subjects.filter((s) => s.kind === "unit");
  const loads = loadsElsewhere
    ? []
    : subjects.filter((s) => s.kind === "consumer" && s.siteAreaId === null);
  const unitRows = units.flatMap((unit) => [
    { subject: unit, nested: false },
    ...subjects
      .filter(
        (s) => s.kind === "consumer" && s.siteAreaId !== null && s.siteAreaId === unit.siteAreaId,
      )
      .map((subject) => ({ subject, nested: true })),
  ]);
  /**
   * **Ein Haus hat keine Wohneinheiten und keinen Netzanteil je Verbraucher**
   * (HM-110). Die Karte heißt dann nach dem, was sie zeigt, und die Spalte
   * „Netzanteil" fällt weg — leer stünde sie wie eine Zahl, die noch kommt.
   */
  const houseOnly = units.length === 0;
  const columns = narrow
    ? "minmax(0, 1fr) auto"
    : houseOnly
      ? "minmax(0, 1.6fr) 1fr 1fr"
      : "minmax(0, 1.6fr) 1fr 1fr 1fr";

  const row = (subject: Subject, last: boolean, nested = false) => {
    const route: Route | null =
      subject.kind !== "unit" || subject.siteAreaId === null
        ? null
        : { kind: "unit", siteId, unitId: subject.siteAreaId, tab: "electricity", period, date };
    return (
      <div
        key={subject.subjectKey}
        role="row"
        style={{
          display: "grid",
          gridTemplateColumns: columns,
          gap: "var(--sp-3)",
          alignItems: "center",
          padding: "11px 0",
          borderBottom: last ? "none" : "1px solid var(--border-subtle)",
        }}
      >
        <span role="cell" style={{ minWidth: 0, paddingLeft: nested ? 18 : 0 }}>
          {route === null ? (
            <span
              {...(nested ? { title: "im Verbrauch der Wohnung darüber enthalten" } : {})}
              style={
                nested
                  ? { fontSize: 13, fontWeight: 500, color: "var(--text-body)" }
                  : { fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)" }
              }
            >
              {nested && (
                <span aria-hidden style={{ color: "var(--text-faint)", marginRight: 6 }}>
                  ↳
                </span>
              )}
              {subject.label}
            </span>
          ) : (
            <a
              href={routePath(route)}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                navigate(route);
              }}
              style={{
                fontSize: 13.5,
                fontWeight: 600,
                color: "var(--text-strong)",
                textDecoration: "none",
              }}
            >
              {subject.label}
            </a>
          )}
          {narrow && (
            <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>
              PV {cell(subject, route === null ? "load_pv_share" : "unit_pv_share")}
              {route === null ? "" : ` · Netz ${cell(subject, "unit_grid_share")}`}
            </span>
          )}
        </span>
        <span
          role="cell"
          style={{
            fontFamily: "var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
            fontSize: 13,
            textAlign: "right",
          }}
        >
          {cell(subject, route === null ? "load_consumption" : "unit_consumption")}
        </span>
        {!narrow && (
          <span
            role="cell"
            style={{
              fontFamily: "var(--font-mono)",
              fontVariantNumeric: "tabular-nums",
              fontSize: 13,
              textAlign: "right",
              color: "var(--hm-quantity-self-consumption)",
            }}
          >
            {cell(subject, route === null ? "load_pv_share" : "unit_pv_share")}
          </span>
        )}
        {!narrow && !houseOnly && (
          <span
            role="cell"
            style={{
              fontFamily: "var(--font-mono)",
              fontVariantNumeric: "tabular-nums",
              fontSize: 13,
              textAlign: "right",
              color: "var(--hm-quantity-grid-import)",
            }}
          >
            {route === null ? "" : cell(subject, "unit_grid_share")}
          </span>
        )}
      </div>
    );
  };

  const header = (
    <div
      role="row"
      style={{
        display: "grid",
        gridTemplateColumns: columns,
        gap: "var(--sp-3)",
        padding: "0 0 8px",
        borderBottom: "1px solid var(--border-subtle)",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "var(--tracking-wide)",
        textTransform: "uppercase",
        color: "var(--text-faint)",
      }}
    >
      <span role="columnheader">{houseOnly ? "Verbraucher" : "Wohneinheit"}</span>
      <span role="columnheader" style={{ textAlign: "right" }}>
        Verbrauch
      </span>
      {!narrow && (
        <span role="columnheader" style={{ textAlign: "right" }}>
          PV-Anteil
        </span>
      )}
      {!narrow && !houseOnly && (
        <span role="columnheader" style={{ textAlign: "right" }}>
          Netzanteil
        </span>
      )}
    </div>
  );

  const title = houseOnly ? "Weitere Verbraucher" : "Wohneinheiten";
  const subtitle = houseOnly
    ? `Verbrauch und PV-Anteil je Verbraucher · ${caption}`
    : `Verbrauch, PV-Anteil und Netzanteil je Wohnung · ${caption}`;

  return (
    <Card title={title} subtitle={subtitle}>
      <div role="table" aria-label={title}>
        {header}
        {unitRows.map(({ subject, nested }, index) =>
          row(subject, index === unitRows.length - 1 && loads.length === 0, nested),
        )}
        {loads.length > 0 && !houseOnly && (
          <div
            style={{
              padding: "12px 0 6px",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "var(--tracking-wide)",
              textTransform: "uppercase",
              color: "var(--text-faint)",
            }}
          >
            Weitere Verbraucher
          </div>
        )}
        {loads.map((subject, index) => row(subject, index === loads.length - 1))}
      </div>
      {/* Nach welcher Regel die Anteile entstanden sind (HM-097) — beim
          Summenzählermodell sind es Summen über den Zeitraum, keine Viertelstunden. */}
      {distribution !== null && (
        <p
          style={{
            marginTop: 12,
            marginBottom: 0,
            fontSize: 12.5,
            lineHeight: 1.55,
            color: "var(--text-muted)",
          }}
        >
          PV-Anteile nach {distributionLabel(distribution)}
          {distribution === "annual_sum"
            ? " — der Anteil am Verbrauch des Zeitraums, angewandt auf den Eigenverbrauch; verbindlich ist die Aufteilung über das Abrechnungsjahr."
            : "."}
        </p>
      )}
    </Card>
  );
}

export function LegendRow({
  color,
  label,
  total,
}: {
  color: string;
  label: string;
  total: Total | undefined;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <span style={{ width: 9, height: 9, borderRadius: 3, flexShrink: 0, background: color }} />
      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "var(--text-body)" }}>
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontVariantNumeric: "tabular-nums",
          fontSize: 12.5,
          fontWeight: 600,
          color: "var(--text-strong)",
        }}
      >
        {total?.value == null ? "–" : `${formatEnergySummary(total.value)} ${ENERGY_UNIT}`}
      </span>
    </div>
  );
}

/**
 * Ein Verhältnis mit seiner Rechnung. Autarkiegrad und Eigenverbrauchsquote
 * haben denselben Zähler und verschiedene Nenner — ohne die Rechnung darunter
 * sind zwei Prozentzahlen nebeneinander nicht auseinanderzuhalten.
 */
export function RatioBlock({
  label,
  total,
  color,
  formula,
  note,
}: {
  label: string;
  total: Total;
  color: string;
  formula: string;
  note: string;
}) {
  /* Kein „mit Ersatzwerten" an der Quote (UI-Durchsicht U10): Sie ist aus den
     Summen der Karten darüber gerechnet, und die tragen die Marke schon. */
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Die Rechnung als Hinweis beim Überfahren, nicht als Formel in Monospace
          unter der Zahl (UI-Durchsicht U11). */}
      <div style={{ fontSize: 12, color: "var(--text-muted)" }} title={formula}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
            fontSize: 22,
            fontWeight: 600,
            color: total.value === null ? "var(--text-faint)" : color,
          }}
        >
          {total.value === null ? "–" : formatPercent(total.value)}
        </span>
      </div>
      <div style={{ fontSize: 11, color: "var(--text-faint)" }}>
        {total.value === null ? "im Zeitraum nicht bestimmbar" : note}
      </div>
    </div>
  );
}
